import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormControlLabelProps,
    FormGroup,
    FormHelperText,
    FormLabel,
} from '@mui/material';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

// ----------------------------------------------------------------------

interface RHFCheckboxProps extends Omit<FormControlLabelProps, 'control'> {
    name: string;
    fullWidth?: boolean;
    helperText?: React.ReactNode;
}

export function RHFCheckbox({ name, helperText, fullWidth = true, ...other }: RHFCheckboxProps) {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <Box
                    sx={{
                        ...(fullWidth && { width: '100%' }),
                    }}
                >
                    <FormControlLabel control={<Checkbox {...field} checked={!!field.value} />} {...other} />

                    {(!!error || helperText) && (
                        <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
                    )}
                </Box>
            )}
        />
    );
}

// ----------------------------------------------------------------------

interface RHFMultiCheckboxProps extends Omit<FormControlLabelProps, 'control' | 'label'> {
    name: string;
    options: { label: string; value: any }[];
    row?: boolean;
    label?: string;
    spacing?: number;
    helperText?: React.ReactNode;
}

export function RHFMultiCheckbox({ row, name, label, options, spacing, helperText, ...other }: RHFMultiCheckboxProps) {
    const { control } = useFormContext();

    const getSelected = (selectedItems: string[], item: string) =>
        selectedItems.includes(item) ? selectedItems.filter(value => value !== item) : [...selectedItems, item];

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FormControl component='fieldset'>
                    {label && (
                        <FormLabel component='legend' sx={{ typography: 'body2' }}>
                            {label}
                        </FormLabel>
                    )}

                    <FormGroup
                        sx={{
                            ...(row && {
                                flexDirection: 'row',
                            }),
                            '& .MuiFormControlLabel-root': {
                                '&:not(:last-of-type)': {
                                    mb: spacing || 0,
                                },
                                ...(row && {
                                    mr: 0,
                                    '&:not(:last-of-type)': {
                                        mr: spacing || 2,
                                    },
                                }),
                            },
                        }}
                    >
                        {options.map(option => (
                            <FormControlLabel
                                key={option.value}
                                control={
                                    <Checkbox
                                        checked={
                                            Array.isArray(field.value) ? field.value.includes(option.value) : false
                                        }
                                        onChange={() =>
                                            Array.isArray(field.value)
                                                ? field.onChange(getSelected(field.value, option.value))
                                                : field.onChange([option.value])
                                        }
                                    />
                                }
                                label={option.label}
                                {...other}
                            />
                        ))}
                    </FormGroup>

                    {(!!error || helperText) && (
                        <FormHelperText error={!!error} sx={{ mx: 0 }}>
                            {error ? error?.message : helperText}
                        </FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
}
